import {observer} from 'mobx-react';
import React from 'react';

import {useMatchMedia} from '@udemy/hooks';
import {useI18n} from '@udemy/i18n';
import {ShoppingClient, ShoppingListName, ShoppingItem} from '@udemy/shopping';
import {AuthenticatedUser, useUDData} from '@udemy/ud-data';

import {getConfig} from 'src/features/cart/config';

import {ShoppingListTrackActionsProps} from '../shopping-list/shopping-list.react-component';
import {ShoppingListWrapper} from './shopping-list-wrapper.react-component';

const listIsHidden = (listName: ShoppingListName) =>
    ShoppingClient.lists[listName].isEmpty && ['saved_for_later', 'wishlist'].includes(listName);

const hasItemsOfType = (items: ShoppingItem[], buyableType: string) =>
    items.some((item) => item.buyable.buyable_object_type === buyableType);

interface ShoppingListPaneProps {
    showSaveForLaterAction?: boolean;
    showCodingExercisesBadge?: boolean;
    trackAction?: ({action, item, destListName}: ShoppingListTrackActionsProps) => void;
}

export const ShoppingListsPane = observer(
    ({
        showSaveForLaterAction = true,
        showCodingExercisesBadge = false,
        trackAction,
    }: ShoppingListPaneProps) => {
        const {me: udMe} = useUDData();
        const {gettext, interpolate, ngettext} = useI18n();
        const config = getConfig(gettext, interpolate, ngettext);
        const isXlMin = useMatchMedia('xl-min');
        const allLists = (udMe as AuthenticatedUser).id
            ? config.shoppingList.all
            : config.shoppingList.all.filter((list) => list !== 'wishlist');
        const renderCartSublists = () => {
            const cartItems = ShoppingClient.lists.cart.items;

            return config.shoppingList.cartSublists
                .filter((sublistType) => hasItemsOfType(cartItems, sublistType))
                .map((sublistType) => (
                    <ShoppingListWrapper
                        key={`cart-${sublistType}`}
                        listName="cart"
                        sublistType={sublistType}
                        showSaveForLaterAction={showSaveForLaterAction}
                        trackAction={trackAction}
                        actionsLayout={isXlMin ? 'vertical' : 'horizontal'}
                        showCodingExercisesBadge={showCodingExercisesBadge}
                    />
                ));
        };

        const visibleLists = allLists.map((listName) => {
            if (listIsHidden(listName as ShoppingListName)) {
                return null;
            }

            if (listName === 'cart' && !ShoppingClient.lists.cart.isEmpty) {
                return renderCartSublists();
            }

            return (
                <ShoppingListWrapper
                    key={listName as ShoppingListName}
                    listName={listName as ShoppingListName}
                    showSaveForLaterAction={showSaveForLaterAction}
                    trackAction={trackAction}
                    actionsLayout={isXlMin ? 'vertical' : 'horizontal'}
                    showCodingExercisesBadge={showCodingExercisesBadge}
                />
            );
        });

        return <>{visibleLists}</>;
    },
);
